import { useId, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Stamp } from "../art/Stamp";
import { DemoFrame } from "./DemoFrame";

const DAYS = [
  { short: "mar.", date: "29/09" },
  { short: "mer.", date: "30/09" },
  { short: "jeu.", date: "01/10" },
  { short: "ven.", date: "02/10" },
  { short: "sam.", date: "03/10" },
  { short: "dim.", date: "04/10" },
  { short: "lun.", date: "05/10" },
];
const PROMISE = 3; // vendredi 02/10

/**
 * Une promesse de paiement, jour par jour : tant qu'elle court, le client
 * sort de « À relancer » ; si la date passe sans règlement, il y revient.
 * Quand le virement arrive dans Pennylane, la facture passe en payée.
 */
export function PromiseDemo() {
  const [day, setDay] = useState(1);
  const [paid, setPaid] = useState(false);
  const sliderId = useId();
  const reduce = useReducedMotion();
  const overdue = !paid && day > PROMISE;
  const state = paid ? "paid" : overdue ? "overdue" : "running";

  return (
    <DemoFrame title="Promesse de paiement · Atelier du Rivage">
      <ol className="grid grid-cols-7 gap-1 text-center" aria-label="Semaine">
        {DAYS.map((d, i) => (
          <li
            key={d.date}
            aria-current={i === day ? "date" : undefined}
            className={cn(
              "relative rounded-md px-0.5 py-2 transition-colors duration-150",
              i === day
                ? "text-primary-foreground delay-[40ms]"
                : i < day
                  ? "text-subtle-foreground"
                  : "text-foreground",
              i === PROMISE && i !== day && "ring-2 ring-action ring-inset",
            )}
          >
            {/* Le repère du jour glisse d'une case à l'autre avec le curseur. */}
            {i === day && (
              <motion.span
                data-day-pill
                layoutId={`${sliderId}-day`}
                aria-hidden="true"
                className="absolute inset-0 rounded-md bg-primary"
                transition={{
                  duration: reduce ? 0 : 0.22,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            )}
            <span className="relative block text-2xs">{d.short}</span>
            <span className="relative block text-ui font-semibold tabular-nums">
              {d.date}
            </span>
          </li>
        ))}
      </ol>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
        <div>
          <label
            htmlFor={sliderId}
            className="text-caption font-semibold text-muted-foreground"
          >
            Aujourd’hui
          </label>
          <input
            id={sliderId}
            type="range"
            min={0}
            max={DAYS.length - 1}
            value={day}
            onChange={(e) => setDay(Number(e.target.value))}
            aria-valuetext={`${DAYS[day].short} ${DAYS[day].date}`}
            className="mt-1 block h-11 w-full cursor-pointer accent-action"
          />
        </div>
        <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 text-ui font-medium text-foreground">
          <input
            type="checkbox"
            checked={paid}
            onChange={(e) => setPaid(e.target.checked)}
            className="size-4 cursor-pointer accent-action"
          />
          Le virement est arrivé
        </label>
      </div>

      <div
        role="status"
        aria-atomic="true"
        className="mt-5 rounded-card border border-border bg-surface p-4"
      >
        {/* Nouvel état : le texte arrive et le statut se pose. */}
        <div key={state} className="enc-rise-sm space-y-3">
          {paid ? (
            <>
              <span className="enc-settle inline-flex">
                <Stamp
                  label="Paiement reçu"
                  date={DAYS[day].date}
                  tone="success"
                />
              </span>
              <p className="text-ui text-foreground">
                Le règlement est dans Pennylane&nbsp;: à la synchronisation
                suivante, la facture passe en payée et Atelier du Rivage sort
                des relances.
              </p>
            </>
          ) : overdue ? (
            <>
              <span className="enc-settle inline-flex">
                <Stamp
                  label="À relancer"
                  date={DAYS[PROMISE].date}
                  tone="danger"
                />
              </span>
              <p className="text-ui text-foreground">
                <strong className="font-semibold">Promesse dépassée</strong>
                &nbsp;: Atelier du Rivage revient dans «&nbsp;À relancer&nbsp;»,
                avec la note de l’appel du 24/09 sous les yeux.
              </p>
            </>
          ) : (
            <>
              <span className="enc-settle inline-flex">
                <Stamp label="Paiement annoncé" date={DAYS[PROMISE].date} />
              </span>
              <p className="text-ui text-foreground">
                Atelier du Rivage sort de «&nbsp;À relancer&nbsp;»&nbsp;: la
                promesse court jusqu’au 2 octobre. Rien à faire d’ici là.
              </p>
            </>
          )}
        </div>
      </div>
    </DemoFrame>
  );
}
